import { _ } from "libs";

import React, { useEffect, useState } from "react";
import withRedux from "../../withRedux";
import { socialFind, loadFavorites, updateFavoriteClubPlayersCount } from "../../actions";
import { Profile, User, Club } from "../../types";
import UsersList from "../usersLists";
import PlayerFilter, { defaultFilter, PlayersFilter } from "../playerFilter";
import PlayerSidebar from "../playerSidebar";
import Dropdown from "../dropdown/dropdown";
import ClubItemMedium from "../items/club/medium";
import Loader from "../loader";
import { persistFilter, hydrateFilter } from "../../libs/filterStorage";
import { InvisibleInfiniteLoader } from "../infiniteLoader";
import NoResults from "../noResults";
import NotInPlayerNetwork from "../notInPlayerNetwork";
import { ProfileState } from "../../reducers/profile";
import { ClubPlayerCountsState } from "../../reducers/clubPlayerCounts";

const ClubDropdown = ({
	clubs,
	selectedClub,
	selectClub,
	playerCounts,
}: {
	clubs: Club[];
	selectedClub: Club | null;
	selectClub: any;
	playerCounts;
}) => {
	const getPlayersCount = (clubId: string): number => {
		if (playerCounts.hasOwnProperty(clubId)) {
			return playerCounts[clubId];
		}

		return null;
	};

	let value = (
		<div className="cell">
			<ClubItemMedium name="&nbsp;" meta="&nbsp;" photoUrl="" />
		</div>
	);

	if (selectedClub) {
		const numberOfPlayers = getPlayersCount(selectedClub.clubInfo.clubId);
		value = (
			<div className="cell">
				<ClubItemMedium
					name={selectedClub.clubInfo.name}
					meta={numberOfPlayers ? _("club.numberOfPlayers", numberOfPlayers, { numberOfPlayers }) : ""}
					photoUrl={selectedClub.clubInfo.photoUrl}
					key={selectedClub.clubInfo.clubId}
				/>
			</div>
		);
	}
	return (
		<Dropdown value={value} extraClass="clubDropdown" onItemSelect={selectClub}>
			{clubs.map((club, index) => {
				const numberOfPlayers = getPlayersCount(club.clubInfo.clubId);
				return (
					<ClubItemMedium
						name={club.clubInfo.name}
						meta={numberOfPlayers ? _("club.numberOfPlayers", numberOfPlayers, { numberOfPlayers }) : ""}
						photoUrl={club.clubInfo.photoUrl}
						key={index}
					/>
				);
			})}
		</Dropdown>
	);
};

function mergeFilterWithParams(filter: PlayersFilter, params) {
	const [ageFrom, ageTo] = filter.age;
	const [defaultAgeFrom, defaultAgeTo] = defaultFilter.age;
	let age = { ageFrom, ageTo } as any;
	if (ageFrom === defaultAgeFrom && ageTo === defaultAgeTo) {
		age = {};
	}
	return { ...params, ...age, gender: filter.gender, skill: filter.skill };
}

const FILTER_NAME = "clubFilter";
const PAGE_LIMIT = 50;

const ClubPage = ({
	profile,
	loadFavorites,
	updateFavoriteClubPlayersCount,
	clubPlayerCounts,
}: {
	profile: Profile;
	loadFavorites: any;
	updateFavoriteClubPlayersCount;
	clubPlayerCounts;
}) => {
	const [loading, setLoading] = useState(false);
	const [favorites, setFavorites] = useState(null as Club[]);
	const [selectedUser, selectUser] = useState(null as User);
	const [selectedClub, selectClub] = useState(null);
	const [clubData, setClubData] = useState({ users: [], playersCount: 0 });
	const [currentPage, setPage] = useState(0);
	const [hasMoreResults, setHasMoreResults] = useState(true);
	const [params, setParams] = useState(
		mergeFilterWithParams(hydrateFilter(FILTER_NAME, defaultFilter, profile.userId), { range: 10 })
	);

	useEffect(() => {
		if (!favorites) {
			loadFavorites().then((favorites: Club[]) => {
				setFavorites(favorites);
				if (favorites.length > 0) {
					selectClub(favorites[0]);
				}
			});
		}
	}, []);

	useEffect(() => {
		setPage(0);
		setClubData({ ...clubData, users: [] });
		setHasMoreResults(true);
		search(0);
	}, [selectedClub, params]);

	useEffect(() => {
		search(0);
	}, [favorites === null]);

	const search = async (page: number) => {
		if (selectedClub) {
			const clubId = selectedClub.clubInfo.clubId;
			setLoading(true);

			try {
				const { data, pageRows, pageCurrent } = await socialFind({ ...params, clubId }, page, PAGE_LIMIT);
				updateFavoriteClubPlayersCount(clubId, pageRows);
				const newUsers = pageCurrent === 0 ? [...data] : [...clubData.users, ...data];
				setClubData({
					users: newUsers,
					playersCount: pageRows,
				});
				if (newUsers.length < pageRows) {
					setPage(currentPage + 1);
				} else {
					setHasMoreResults(false);
				}
			} catch (error) {
				console.log(error);
			} finally {
				setLoading(false);
			}
		}
	};

	const onViewChange = async (inView: boolean, observerEntry) => {
		const shouldLoad = inView && hasMoreResults;

		if (shouldLoad) {
			search(currentPage);
		}
	};

	const onFilterChange = (filter: PlayersFilter) => {
		persistFilter(FILTER_NAME, filter, profile.userId);
		setParams(mergeFilterWithParams(filter, params));
	};

	const closePlayerSidebar = () => {
		selectUser(null);
	};

	const getInnerContent = (): JSX.Element | JSX.Element[] => {
		if (!profile.useNetwork) {
			return <NotInPlayerNetwork />;
		}

		if (!loading && favorites && favorites.length === 0) {
			return (
				<div className="noResults">
					<h5>{_("players.club.noResults.heading")}</h5>
					<p>{_("players.club.noResults.description")}</p>
				</div>
			);
		}

		return [
			<div className="playersHeader" key="header">
				<ClubDropdown
					playerCounts={clubPlayerCounts || {}}
					clubs={favorites || []}
					selectedClub={selectedClub}
					selectClub={selectClub}
				/>
				<PlayerFilter
					onFilterChange={onFilterChange}
					value={hydrateFilter(FILTER_NAME, defaultFilter, profile.userId)}
				/>
			</div>,
			<div className="playersBody" key="body">
				{clubData.users && clubData.users.length > 0 ? (
					<UsersList users={clubData.users || []} onSelect={selectUser} selectedUser={selectedUser} />
				) : null}
				{clubData.users && clubData.users.length === 0 && !loading ? <NoResults /> : null}
				<InvisibleInfiniteLoader onViewChange={onViewChange} />
				{loading ? <Loader /> : null}
			</div>,
		];
	};

	return (
		<div className="contentWrapper clubPlayersPage">
			<div className="contentLeft">{getInnerContent()}</div>
			<div className="contentRight">
				{selectedUser ? <PlayerSidebar user={selectedUser} onClose={closePlayerSidebar} /> : null}
			</div>
		</div>
	);
};

const mapStateToProps = (
	{ profile, clubPlayerCounts }: { profile: ProfileState; clubPlayerCounts: ClubPlayerCountsState },
	ownProps
) => {
	return { profile, clubPlayerCounts };
};

export default withRedux({ loadFavorites, updateFavoriteClubPlayersCount }, mapStateToProps)(ClubPage);
